import React from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { useSelector } from "react-redux"
import { LoadingInner } from "#/components/views/Loading"
import { PageHeader } from "#/features/global-ui/page-header"
import { PageLayout } from "#/features/global-ui/page-layout"
import { getMemberFullName } from "#/redux/derivedSelectors"
import { getPreferredName } from "#/redux/reducers/userInfo"
import { Title } from "#/services/page-title/Title"
import { PiiText } from "#/services/tracking"

// The consumer package is code-split and lazy-loaded (mirroring Root.tsx and the subscription
// billing page), so none of the package's code loads until a consumer-program member lands here.
// Two entry points because the page fills both of `PageLayout variant="sidebar"`'s slots, the same
// way the enterprise home splits into HomeMain / HomeSidebar. Both resolve from the same chunk.
const ConsumerCarrotPlanPage = React.lazy(() =>
  import("@internal/consumer").then((m) => ({ default: m.ConsumerCarrotPlanPage }))
)
const ConsumerCarrotPlanSidebar = React.lazy(() =>
  import("@internal/consumer").then((m) => ({ default: m.ConsumerCarrotPlanSidebar }))
)

export function ConsumerHomePage(): JSX.Element {
  const intl = useIntl()
  const memberFullName = useSelector(getMemberFullName)
  const preferredName = useSelector(getPreferredName)
  const displayName = preferredName ? preferredName : memberFullName

  return (
    <PageLayout
      variant="sidebar"
      sidebar={
        // No fallback of its own: it shares the main column's chunk, so the spinner there already
        // covers the wait and a second one would just flash alongside it.
        <React.Suspense fallback={null}>
          <ConsumerCarrotPlanSidebar />
        </React.Suspense>
      }
      header={
        <PageHeader
          pageTitle={
            <FormattedMessage
              defaultMessage="Hello, {displayName}"
              values={{ displayName: <PiiText>{displayName}</PiiText> }}
            />
          }
        />
      }
    >
      <Title title={intl.formatMessage({ defaultMessage: "Your Carrot Plan" })} />
      <React.Suspense fallback={<LoadingInner />}>
        <ConsumerCarrotPlanPage />
      </React.Suspense>
    </PageLayout>
  )
}
